import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { formatSize } from 'common/packages';

const RenameAssetDialog = (props) => {
  const { open, pkg, values, saving, onCancel, onConfirm } = props;

  const [strategy, setStrategy] = useState('move');
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (open) {
      setStrategy('move');
      setFile(null);
    }
  }, [open]);

  const changes = [];
  if (values && values.fileName !== (pkg.file_name || '')) {
    changes.push(`file name ${pkg.file_name || '(none)'} → ${values.fileName || '(none)'}`);
  }
  if (values && values.type !== (pkg.type || '')) {
    changes.push(`type ${pkg.type || '(none)'} → ${values.type || '(none)'}`);
  }

  const canConfirm = !saving && (strategy !== 'reupload' || Boolean(file));

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>What should happen to the stored file?</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          You are changing the {changes.join(' and ')}. The storage path is derived from
          the type and file name, so it no longer matches where the artifact currently
          sits.
        </DialogContentText>

        <RadioGroup value={strategy} onChange={(event) => setStrategy(event.target.value)}>
          <FormControlLabel
            value="move"
            control={<Radio />}
            label="Move the stored file to the new name"
          />
          <FormControlLabel
            value="keep"
            control={<Radio />}
            label="Leave it where it is"
          />
          <FormControlLabel
            value="reupload"
            control={<Radio />}
            label="Upload a new file"
          />
        </RadioGroup>

        <Stack spacing={2} sx={{ mt: 1 }}>
          {strategy === 'keep' && (
            <Alert severity="warning">
              The stored object keeps its current path, so the path will stop matching
              the file name shown here. Downloads keep working; anyone reading the
              storage bucket directly will not find the file under the name this package
              advertises.
            </Alert>
          )}

          {strategy === 'reupload' && (
            <Stack spacing={1}>
              <Typography variant="body2">
                The new file is uploaded straight to storage after the metadata is
                saved.
              </Typography>
              <input
                type="file"
                onChange={(event) => setFile(event.target.files[0] || null)}
              />
              {file && (
                <Typography variant="body2" color="text.secondary">
                  {file.name} — {formatSize(file.size)}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => onConfirm(strategy, file)}
          disabled={!canConfirm}
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RenameAssetDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  pkg: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  values: PropTypes.object,
};

export default RenameAssetDialog;
